import { useEffect, useCallback } from 'react'
import { usePopoverHover } from 'minimal-shared/hooks'
import { isActiveLink, isExternalLink } from 'minimal-shared/utils'

import { useTheme } from '@mui/material/styles'

import { usePathname } from 'src/routes/hooks'

import { NavItem } from './nav-item'
import { navSectionClasses } from '../styles'
import { NavUl, NavLi, NavDropdown, NavDropdownPaper } from '../components'

// ----------------------------------------------------------------------

export function NavList({
  data,
  depth,
  render,
  cssVars,
  slotProps,
  enabledRootRedirect
}) {
  const theme = useTheme()

  const pathname = usePathname()

  const isActive = isActiveLink(pathname, data.path, !!data.children)

  const {
    open,
    onOpen,
    onClose,
    anchorEl,
    elementRef: navItemRef
  } = usePopoverHover()

  const isRtl = theme.direction === 'rtl'
  const id = open ? `${data.title}-popover` : undefined

  useEffect(() => {
    // If the pathname changes, close the menu
    if (open) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleOpenMenu = useCallback(() => {
    if (data.children) {
      onOpen()
    }
  }, [data.children, onOpen])

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      aria-describedby={id}
      // slots
      path={data.path}
      onClickNav={data.onClickNav}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      active={isActive}
      open={open}
      disabled={data.disabled}
      // options
      depth={depth}
      render={render}
      hasChild={!!data.children}
      externalLink={data.external || isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onMouseEnter={handleOpenMenu}
      onMouseLeave={onClose}
    />
  )

  const renderDropdown = () =>
    !!data.children && (
      <NavDropdown
        disableScrollLock
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: isRtl ? 'left' : 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: isRtl ? 'right' : 'left'
        }}
        slotProps={{
          paper: {
            onMouseEnter: handleOpenMenu,
            onMouseLeave: onClose,
            className: navSectionClasses.dropdown.root
          }
        }}
        sx={{ ...cssVars }}
      >
        <NavDropdownPaper
          className={navSectionClasses.dropdown.paper}
          sx={slotProps?.dropdown?.paper}
        >
          <NavSubList
            data={data.children}
            depth={depth}
            render={render}
            cssVars={cssVars}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
          />
        </NavDropdownPaper>
      </NavDropdown>
    )

  return (
    <NavLi disabled={data.disabled}>
      {renderNavItem()}
      {/*
       * TODO: Should be removed in MUI next.
       * Add `open` condition to disable transition effect on close.
       * https://github.com/mui/material-ui/issues/43106
       */}
      {open && renderDropdown()}
    </NavLi>
  )
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  render,
  cssVars,
  depth = 0,
  slotProps,
  enabledRootRedirect
}) {
  return (
    <NavUl sx={{ gap: 0.5 }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          cssVars={cssVars}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  )
}
